import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import {
  getVolunteerOpportunities,
  getVolunteerAllEventRegistrations,
  getEvent,
  withdrawVolunteerSignup,
  cancelRegistration,
  getQRCodeUrl,
  fmtDateTime,
  daysUntil,
  EVENT_CATEGORIES,
} from '../../../api/eventsApi';

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_COLORS = {
  CIRCLE_TIME:         '#0D9488',
  TALK_WORKSHOP:       '#4338CA',
  SOCIAL_RECREATIONAL: '#F97316',
  OTHER:               '#6B7280',
};

const DAYS = [
  { key: 'monday',    label: 'Monday'    },
  { key: 'tuesday',   label: 'Tuesday'   },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday',  label: 'Thursday'  },
  { key: 'friday',    label: 'Friday'    },
  { key: 'saturday',  label: 'Saturday'  },
  { key: 'sunday',    label: 'Sunday'    },
];

const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

// ── SVG Icons ─────────────────────────────────────────────────────────────────

const IC = { display: 'inline', verticalAlign: 'middle' };
const svg = (paths, size, color) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={IC}>
    {paths}
  </svg>
);

const IconCalendar = ({ size = 14, color = 'currentColor' }) => svg(<>
  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
  <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
</>, size, color);

const IconClock = ({ size = 14, color = 'currentColor' }) => svg(<>
  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
</>, size, color);

const IconMapPin = ({ size = 14, color = 'currentColor' }) => svg(<>
  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
</>, size, color);

const IconPin = ({ size = 14, color = 'currentColor' }) => svg(<>
  <line x1="12" y1="17" x2="12" y2="22"/>
  <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17z"/>
</>, size, color);

const IconUser = ({ size = 14, color = 'currentColor' }) => svg(<>
  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
</>, size, color);

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function catLabel(value) {
  return EVENT_CATEGORIES.find(c => c.value === value)?.label || value;
}

// ── My Events section with sub-pills ──────────────────────────────────────────

function MyEventsSection({ orgId, volunteerId, volunteerName }) {
  const [subTab, setSubTab] = useState('Registered Events');
  const [actionMsg, setActionMsg] = useState('');

  // ── Volunteered Events state ──────────────────────────────────────────────
  const [volunteeredEvents, setVolunteeredEvents] = useState([]);
  const [volLoading, setVolLoading]               = useState(true);
  const [volError, setVolError]                   = useState('');
  const [withdrawing, setWithdrawing]             = useState({});

  // ── Registered Events state ───────────────────────────────────────────────
  const [registeredEvents, setRegisteredEvents] = useState([]);
  const [regLoading, setRegLoading]             = useState(true);
  const [regError, setRegError]                 = useState('');
  const [cancelTarget, setCancelTarget]         = useState(null);
  const [cancelReason, setCancelReason]         = useState('');
  const [cancelLoading, setCancelLoading]       = useState(false);
  const [qrModal, setQrModal]                   = useState(null);

  const showMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(''), 3500);
  };

  const loadVolunteered = useCallback(async () => {
    if (!orgId || !volunteerId) { setVolLoading(false); return; }
    setVolLoading(true); setVolError('');
    try {
      const snap = await getDocs(
        query(
          collection(db, 'volunteer_signups'),
          where('orgId', '==', orgId),
          where('volunteer_id', '==', volunteerId)
        )
      );
      const signups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const enriched = await Promise.all(
        signups.map(async signup => {
          const evt = await getEvent(orgId, signup.event_id);
          return evt ? { signup, event: evt } : null;
        })
      );
      setVolunteeredEvents(
        enriched
          .filter(Boolean)
          .filter(({ event }) => ['PUBLISHED', 'COMPLETED'].includes(event.status))
          .sort((a, b) => {
            const toMs = ts => ts?.toDate ? ts.toDate().getTime() : new Date(ts || 0).getTime();
            return toMs(a.event.start_datetime) - toMs(b.event.start_datetime);
          })
      );
    } catch {
      setVolError('Failed to load your volunteer sign-ups.');
    } finally {
      setVolLoading(false);
    }
  }, [orgId, volunteerId]);

  const loadRegistered = useCallback(async () => {
    if (!orgId || !volunteerId) { setRegLoading(false); return; }
    setRegLoading(true); setRegError('');
    try {
      const regs = await getVolunteerAllEventRegistrations(orgId, volunteerId);
      const enriched = await Promise.all(
        regs
          .filter(r => r.status !== 'CANCELLED')
          .map(async reg => {
            const evt = await getEvent(orgId, reg.event_id);
            return evt ? { reg, event: evt } : null;
          })
      );
      setRegisteredEvents(
        enriched
          .filter(Boolean)
          .sort((a, b) => {
            const toMs = ts => ts?.toDate ? ts.toDate().getTime() : new Date(ts || 0).getTime();
            return toMs(a.event.start_datetime) - toMs(b.event.start_datetime);
          })
      );
    } catch {
      setRegError('Failed to load your event registrations.');
    } finally {
      setRegLoading(false);
    }
  }, [orgId, volunteerId]);

  useEffect(() => {
    loadVolunteered();
    loadRegistered();
  }, [loadVolunteered, loadRegistered]);

  const handleWithdraw = async (signup, event) => {
    if (!window.confirm(`Withdraw your volunteer sign-up for "${event.title}"?`)) return;
    setWithdrawing(p => ({ ...p, [signup.id]: true }));
    try {
      await withdrawVolunteerSignup(orgId, event.id, volunteerId);
      showMsg(`Withdrawn from "${event.title}".`);
      await loadVolunteered();
    } catch {
      showMsg('Withdrawal failed. Please try again.');
    } finally {
      setWithdrawing(p => ({ ...p, [signup.id]: false }));
    }
  };

  const handleCancelReg = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelLoading(true);
    try {
      await cancelRegistration(orgId, cancelTarget.event.id, cancelTarget.reg.id, cancelReason, volunteerId);
      showMsg(`Registration for "${cancelTarget.event.title}" cancelled.`);
      setCancelTarget(null); setCancelReason('');
      await loadRegistered();
    } catch {
      showMsg('Cancellation failed. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  // ── Sub-pill bar ──────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['Registered Events', 'Volunteered Events'].map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            style={{
              padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
              border: `1.5px solid ${subTab === tab ? 'var(--org-primary, #3A7D44)' : '#D1D5DB'}`,
              background: subTab === tab ? 'var(--org-primary, #3A7D44)' : '#fff',
              color: subTab === tab ? '#fff' : '#374151',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {actionMsg && (
        <div style={{ marginBottom: 14, padding: '8px 14px', background: '#D1FAE5', borderRadius: 6, fontSize: 13, color: '#065F46' }}>
          {actionMsg}
        </div>
      )}

      {/* ── Volunteered Events ── */}
      {subTab === 'Volunteered Events' && (
        volLoading ? <SkeletonList /> :
        volError   ? <div style={{ color: '#DC2626', fontSize: 13 }}>{volError}</div> :
        volunteeredEvents.length === 0 ? (
          <div className="bb-member-home-empty">
            <div className="bb-member-home-empty-icon"><IconCalendar size={40} color="#9CA3AF" /></div>
            <p className="bb-member-home-empty-msg">No volunteer sign-ups yet.</p>
            <p className="bb-member-home-empty-sub">Browse the Events Management tab to find opportunities.</p>
          </div>
        ) : (
          volunteeredEvents.map(({ signup, event }) => {
            const catColor = CATEGORY_COLORS[event.category] || '#6B7280';
            const days     = daysUntil(event.start_datetime);
            const isFull   = (event.volunteer_signup_count || 0) >= (event.volunteers_needed || 0);
            const busy     = !!withdrawing[signup.id];
            return (
              <div key={signup.id} style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, background: '#fff' }}>
                <div style={{ background: catColor, height: 6 }} />
                <div style={{ padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: catColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {catLabel(event.category)}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#D1FAE5', color: '#065F46' }}>
                          You're Signed Up
                        </span>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{event.title}</div>
                      <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 2 }}>
                        {fmtDateTime(event.start_datetime)}{event.end_datetime && ` – ${fmtDateTime(event.end_datetime)}`}
                      </div>
                      {event.location?.name && (
                        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 6 }}>
                          📍 {event.location.name}{event.location.address ? `, ${event.location.address}` : ''}
                        </div>
                      )}
                      {event.volunteers_needed != null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                          <span style={{ fontSize: 12, color: '#6B7280' }}>Volunteers needed:</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: isFull ? '#059669' : '#92400E' }}>
                            {event.volunteer_signup_count || 0} / {event.volunteers_needed}
                          </span>
                          <div style={{ flex: 1, maxWidth: 100, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: 3,
                              background: isFull ? '#059669' : '#6B46C1',
                              width: `${Math.min(((event.volunteer_signup_count || 0) / (event.volunteers_needed || 1)) * 100, 100)}%`,
                              transition: 'width 0.3s',
                            }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                      {days !== null && days > 0 && (
                        <span style={{ fontSize: 11, color: '#6B7280' }}>in {days}d</span>
                      )}
                      <button
                        onClick={() => handleWithdraw(signup, event)}
                        disabled={busy}
                        style={{
                          padding: '6px 14px', borderRadius: 6,
                          border: '1.5px solid #DC2626', background: '#fff',
                          color: '#DC2626', fontSize: 12, fontWeight: 600,
                          cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
                        }}
                      >
                        {busy ? 'Withdrawing…' : 'Withdraw Volunteering'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )
      )}

      {/* ── Registered Events ── */}
      {subTab === 'Registered Events' && (
        regLoading ? <SkeletonList /> :
        regError   ? <div style={{ color: '#DC2626', fontSize: 13 }}>{regError}</div> :
        registeredEvents.length === 0 ? (
          <div className="bb-member-home-empty">
            <div className="bb-member-home-empty-icon"><IconCalendar size={40} color="#9CA3AF" /></div>
            <p className="bb-member-home-empty-msg">No event registrations yet.</p>
            <p className="bb-member-home-empty-sub">Browse the Events Management tab to register for events.</p>
          </div>
        ) : (
          registeredEvents.map(({ reg, event }) => {
            const catColor = CATEGORY_COLORS[event.category] || '#6B7280';
            const days     = daysUntil(event.start_datetime);
            const qrUrl    = reg.qr_code_token ? getQRCodeUrl(reg.qr_code_token, 200) : null;
            return (
              <div key={reg.id} style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, background: '#fff' }}>
                <div style={{ background: catColor, height: 6 }} />
                <div style={{ padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: catColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {catLabel(event.category)}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#D1FAE5', color: '#065F46' }}>
                          Registered
                        </span>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{event.title}</div>
                      <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 2 }}>
                        {fmtDateTime(event.start_datetime)}{event.end_datetime && ` – ${fmtDateTime(event.end_datetime)}`}
                      </div>
                      {event.location?.name && (
                        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                          📍 {event.location.name}{event.location.address ? `, ${event.location.address}` : ''}
                        </div>
                      )}
                      {event.volunteer_capacity != null && (
                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                          Volunteer attendee slots: <strong style={{ color: '#4338CA' }}>{event.volunteer_capacity}</strong>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                      {days !== null && days > 0 && (
                        <span style={{ fontSize: 11, color: '#6B7280' }}>in {days}d</span>
                      )}
                      <button
                        onClick={() => { setCancelTarget({ reg, event }); setCancelReason(''); }}
                        style={{
                          padding: '6px 14px', borderRadius: 6,
                          border: '1.5px solid #DC2626', background: '#fff',
                          color: '#DC2626', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        Cancel Registration
                      </button>
                      {qrUrl && (
                        <button
                          onClick={() => setQrModal({ url: qrUrl, title: event.title })}
                          style={{
                            padding: '6px 14px', borderRadius: 6,
                            border: '1.5px solid #4338CA', background: '#EEF2FF',
                            color: '#4338CA', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          View QR Code
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )
      )}

      {/* ── Cancel registration modal ── */}
      {cancelTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#111827' }}>Cancel Registration</h3>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px' }}>
              Cancel your registration for <strong>{cancelTarget.event.title}</strong>? Please give a reason:
            </p>
            <textarea
              rows={3}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation…"
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setCancelTarget(null); setCancelReason(''); }}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', fontSize: 13, cursor: 'pointer' }}
              >
                Back
              </button>
              <button
                onClick={handleCancelReg}
                disabled={cancelLoading || !cancelReason.trim()}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: cancelLoading || !cancelReason.trim() ? '#FCA5A5' : '#DC2626',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: cancelLoading || !cancelReason.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {cancelLoading ? 'Cancelling…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR Code modal ── */}
      {qrModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 300, width: '90%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#111827' }}>Your QR Code</h3>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 16px' }}>{qrModal.title}</p>
            <img src={qrModal.url} alt="QR Code" style={{ width: 200, height: 200, borderRadius: 8, border: '1px solid #E5E7EB' }} />
            <p style={{ fontSize: 11, color: '#9CA3AF', margin: '12px 0 0' }}>Show this at the event for check-in</p>
            <button
              onClick={() => setQrModal(null)}
              style={{ marginTop: 16, padding: '8px 28px', borderRadius: 6, border: 'none', background: '#4338CA', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function VolunteerHome({
  orgId, volunteerId, volunteerName,
  officeHours, orgName, orgConfig,
  activeTab, scrollSignal,
}) {
  // Section refs for scroll-to
  const myEventsRef    = useRef(null);
  const oppsRef        = useRef(null);
  const officeHoursRef = useRef(null);

  const refMap = {
    'My Events':     myEventsRef,
    'Opportunities': oppsRef,
    'Office Hours':  officeHoursRef,
  };

  useEffect(() => {
    if (!scrollSignal) return;
    const target = refMap[activeTab];
    if (target?.current) {
      setTimeout(() => {
        if (target.current) target.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [scrollSignal]); // eslint-disable-line

  // ── Volunteer Opportunities ─────────────────────────────────────────────────
  const [opps,        setOpps]        = useState([]);
  const [oppsLoading, setOppsLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    getVolunteerOpportunities(orgId)
      .then(data => setOpps((data || []).slice(0, 5)))
      .catch(err => { console.error('VolunteerHome opps error:', err); setOpps([]); })
      .finally(() => setOppsLoading(false));
  }, [orgId]);

  // ── Office Hours derived ────────────────────────────────────────────────────
  const schedule = officeHours?.schedule || null;
  const closures = (officeHours?.specialClosures || [])
    .filter(c => c.date >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => a.date.localeCompare(b.date));

  const todayDayKey   = DAY_KEYS[new Date().getDay()];
  const todayStr      = new Date().toISOString().slice(0, 10);
  const todayClosure  = (officeHours?.specialClosures || []).find(c => c.date === todayStr);
  const todaySchedule = schedule?.[todayDayKey];
  const todayStatus   = todayClosure
    ? { open: false, reason: todayClosure.name || 'Special Closure' }
    : todaySchedule?.open
      ? { open: true, openTime: todaySchedule.openTime, closeTime: todaySchedule.closeTime }
      : { open: false, reason: 'Closed today' };

  // ── Org branding ────────────────────────────────────────────────────────────
  const galleryUrls    = orgConfig?.branding?.images?.gallery || [];
  const logoUrl        = orgConfig?.branding?.logo?.url       || null;
  const displayOrgName = orgConfig?.branding?.name || orgConfig?.aboutUs?.name || orgName || orgId;

  return (
    <div className="bb-member-home-page">

      {/* ── Logo header ──────────────────────────────────────────── */}
      {logoUrl ? (
        <div className="bb-member-home-logo-header">
          <img src={logoUrl} alt="Organisation logo" className="bb-member-home-logo-centered" />
        </div>
      ) : (
        <div className="bb-member-home-welcome-bar">
          <div>
            <div className="bb-member-home-welcome-org">{displayOrgName}</div>
            <div className="bb-member-home-welcome-sub">Volunteer Portal</div>
          </div>
        </div>
      )}

      {/* ── Gallery ──────────────────────────────────────────────── */}
      {galleryUrls.length > 0 && (
        <div className="bb-member-home-gallery">
          {galleryUrls.map((url, i) => (
            <img key={i} src={url} alt={`Gallery ${i + 1}`} className="bb-member-home-gallery-img" />
          ))}
        </div>
      )}

      {/* ── My Events (with Registered / Volunteered sub-tabs) ────── */}
      <section ref={myEventsRef} id="section-my-events" className="bb-member-home-card">
        <div className="bb-member-home-heading">
          <h2>My Events</h2>
        </div>
        <MyEventsSection orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
      </section>

      {/* ── Volunteer Opportunities ─────────────────────────────── */}
      <section ref={oppsRef} id="section-opportunities" className="bb-member-home-card">

        <div className="bb-member-home-heading">
          <h2>Volunteer Opportunities</h2>
        </div>

        {oppsLoading ? (
          <SkeletonList />
        ) : opps.length === 0 ? (
          <div className="bb-member-home-empty">
            <div className="bb-member-home-empty-icon"><IconUser size={40} color="#9CA3AF" /></div>
            <p className="bb-member-home-empty-msg">No open opportunities right now.</p>
            <p className="bb-member-home-empty-sub">Check back soon — new events needing volunteers will appear here.</p>
          </div>
        ) : (
          <div className="bb-member-home-event-list">
            {opps.map(ev => <EventCard key={ev.id} event={ev} />)}
          </div>
        )}
      </section>

      {/* ── Office Hours ────────────────────────────────────────── */}
      <section ref={officeHoursRef} id="section-office-hours" className="bb-member-home-card">

        <div className="bb-member-home-heading">
          <h2>Office Hours</h2>
        </div>

        {!schedule ? (
          <div className="bb-member-home-empty">
            <div className="bb-member-home-empty-icon"><IconClock size={40} color="#9CA3AF" /></div>
            <p className="bb-member-home-empty-msg">Office hours not configured yet.</p>
            <p className="bb-member-home-empty-sub">Please contact {displayOrgName} directly for availability.</p>
          </div>
        ) : (
          <>
            {/* Today's status banner */}
            <div className={`bb-member-home-today-banner${todayStatus.open ? ' open' : ' closed'}`}>
              <span className="bb-member-home-today-dot" />
              <span className="bb-member-home-today-status">
                {todayStatus.open ? 'Open Today' : 'Closed Today'}
              </span>
              <span className="bb-member-home-today-detail">
                {todayStatus.open
                  ? `${fmt12(todayStatus.openTime)} – ${fmt12(todayStatus.closeTime)}`
                  : todayStatus.reason}
              </span>
            </div>

            <div className="bb-member-home-hours-grid">
              {/* Weekly schedule */}
              <div>
                <div className="bb-member-home-sub-label">Weekly Schedule</div>
                <div className="bb-member-home-hours-list">
                  {DAYS.map(({ key, label }) => {
                    const day     = schedule[key];
                    const isToday = todayDayKey === key;
                    return (
                      <div key={key} className={`bb-member-home-hours-row${isToday ? ' today' : ''}`}>
                        <span className="bb-member-home-hours-day">
                          {label}
                          {isToday && <span className="bb-member-home-today-pill">Today</span>}
                        </span>
                        <span className={`bb-member-home-hours-val${!day?.open ? ' closed' : ''}`}>
                          {day?.open ? `${fmt12(day.openTime)} – ${fmt12(day.closeTime)}` : 'Closed'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Upcoming closures */}
              <div>
                <div className="bb-member-home-sub-label">Upcoming Closures</div>
                {closures.length === 0 ? (
                  <p className="bb-member-home-no-closures">No upcoming closures scheduled.</p>
                ) : (
                  <div className="bb-member-home-closures">
                    {closures.slice(0, 6).map(c => (
                      <div key={c.id || c.date} className="bb-member-home-closure-row">
                        <div className="bb-member-home-closure-date-block">
                          <span className="bb-member-home-closure-month">
                            {new Date(c.date + 'T00:00:00').toLocaleDateString('en-SG', { month: 'short' })}
                          </span>
                          <span className="bb-member-home-closure-day">
                            {new Date(c.date + 'T00:00:00').getDate()}
                          </span>
                        </div>
                        <div>
                          <div className="bb-member-home-closure-name">{c.name}</div>
                          {c.note && <div className="bb-member-home-closure-note">{c.note}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {officeHours?.notes && (
                  <div className="bb-member-home-oh-note">
                    <span className="bb-member-home-oh-note-icon"><IconPin size={14} color="#9CA3AF" /></span>
                    <p>{officeHours.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>

    </div>
  );
}

// ── Event card (Opportunities section) ────────────────────────────────────────

function EventCard({ event }) {
  const color = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.OTHER;
  return (
    <div className="bb-member-home-event-card">
      <div className="bb-member-home-event-stripe" style={{ background: color }} />
      <div className="bb-member-home-event-body">
        <div className="bb-member-home-event-title">{event.title}</div>
        <div className="bb-member-home-event-meta">
          <IconCalendar /> {fmtDateTime(event.start_datetime)}
          {event.location?.name && <span style={{ marginLeft: 12 }}><IconMapPin /> {event.location.name}</span>}
          {event.volunteers_required && (
            <span style={{ marginLeft: 12, color: '#059669', fontWeight: 600 }}>
              <IconUser color="#059669" /> Volunteers needed
            </span>
          )}
        </div>
      </div>
      <span className="bb-member-home-event-cat" style={{ background: color }}>
        {(event.category || 'OTHER').replace(/_/g, ' ')}
      </span>
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────

function SkeletonList() {
  return (
    <div className="skel-appt-list">
      {[1, 2].map(i => (
        <div key={i} className="skel-appt-card">
          <div className="skel-avatar" />
          <div className="skel-lines">
            <div className="skel-line skel-line--lg" />
            <div className="skel-line skel-line--md" />
            <div className="skel-line skel-line--sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default VolunteerHome;
